import React, { FC, useMemo } from "react"
import { Navigate, useParams } from "react-router-dom"

import { Divider, Grid } from "@mantine/core"

import { useDevices, useGroups } from "../contexts/devices-context"
import { FullscreenLoader } from "../components/fallbacks/FullscreenLoader"
import Page from "../components/layouts/page/Page"
import LightModuleSettings from "../features/modules/components/lights-module-settings/LightModuleSettings"
import CameraModuleSettings from "../features/modules/components/camera-module-settings/CameraModuleSettings"
import OperatingTimeModuleSettings from "../features/modules/components/operating-time-module-settings/OperatingTimeModuleSettings"
import UploadModuleSettings from "../features/modules/components/upload-module-settings/UploadModuleSettings"
import AudioModuleSettings from "../features/modules/components/audio-module-settings/AudioModuleSettings"
import { useLanguage } from "../contexts/language-context"
import PageDivider from "../components/layouts/page/PageDivider"
import DeviceSummaryCard from "../features/devices/components/device-summary-card/DeviceSummaryCard"

const Group: FC = () => {
    const { id } = useParams()
    const { map, loading } = useGroups()
    const { data } = useDevices()
    const { L } = useLanguage()
    const group = useMemo(() => (id ? map[id] : null), [map, id])
    const devices = useMemo(() => data.filter(device => device.group === group?.id), [data, group])
    const moduleNames = useMemo(
        () => [...new Set(
            devices.flatMap(device =>
                device.modules.map(module => module.name)
            )
        )],
        [devices]
    )

    return (
        <Page
            title={group?.name ?? ""}
            returnAction={L("Groups")}
        >
            {loading ? (
                <FullscreenLoader />
            ) : group ? (
                <Grid>
                    <Grid.Col span={12} mt="7rem">
                        <PageDivider>{L("Devices")}</PageDivider>
                    </Grid.Col>
                    {devices.map(device => (
                        <Grid.Col span={12} sm={6} lg={4} key={device.id}>
                            <DeviceSummaryCard device={device} />
                        </Grid.Col>
                    ))}
                    <Grid.Col span={12}>
                        <PageDivider>{L("Settings")}</PageDivider>
                    </Grid.Col>
                    {moduleNames?.includes("lights") && (
                        <Grid.Col span={12} lg={6}>
                            <LightModuleSettings.Group group={group} />
                        </Grid.Col>
                    )}
                    {moduleNames?.includes("audio") && (
                        <Grid.Col span={12} lg={6}>
                            <AudioModuleSettings.Group group={group} />
                        </Grid.Col>
                    )}
                    {moduleNames?.includes("camera") && (
                        <Grid.Col span={12} lg={6}>
                            <CameraModuleSettings.Group group={group} />
                        </Grid.Col>
                    )}
                    {moduleNames?.includes("wittypi") && (
                        <Grid.Col span={12} lg={6}>
                            <OperatingTimeModuleSettings.Group group={group} />
                        </Grid.Col>
                    )}
                    {moduleNames?.includes("upload_client") && (
                        <Grid.Col span={12} lg={6}>
                            <UploadModuleSettings.Group group={group} />
                        </Grid.Col>
                    )}
                </Grid>
            ) : (
                <Navigate to=".." relative="path" />
            )}
        </Page>
    )
}

export default Group
